import { hexToRgb } from "../../utils/colorConverters";
import { editableElements, editableSettings, page_keys } from "./config";
import { defaultData, editOrderLinesConfirmMap, editOrderLinesMap, editRegisterReturnMap, editReturnStatusPageMap, editReturnSubmittedMap } from "./Mastermaps";

const normalizeEditable = (editable) => {
    if (Array.isArray(editable)) {
        return editable.reduce((acc, element) => {
            acc[element] = '';
            return acc;
        }, {});
    } else if (typeof editable === 'object' && editable !== null) {
        return Object.entries(editable).reduce((acc, [groupKey, elements]) => {
            acc[groupKey] = elements.reduce((groupAcc, element) => {
                groupAcc[element] = '';
                return groupAcc;
            }, {});
            return acc;
        }, {});
    } else {
        return {};
    }
};

//Creates the initial map
export const initialCreateMap = (givenMap) => {
    const map = {};
    Object.keys(givenMap).forEach(key => {
        const portalKey = givenMap[key];
        const editableRaw = editableSettings[portalKey]?.editable || [];
        const extraClasses = editableSettings[portalKey]?.extraClasses || {};

        let editableValues = {};

        if (Array.isArray(editableRaw)) {
            editableValues = normalizeEditable(editableRaw);
        } else if (typeof editableRaw === 'object') {
            // Flatten the grouped structure with dot notation or nested as-is
            Object.entries(editableRaw).forEach(([group, elements]) => {
                editableValues[group] = normalizeEditable(elements);
            });
        }

        const portalValueMap = {
            editableValues,
        };

        if (Object.keys(extraClasses).length > 0) {
            portalValueMap.extraClasses = Object.entries(extraClasses).reduce((acc, [extraClassKey, editableElementsForClass]) => {
                acc[extraClassKey] = editableElementsForClass.reduce((classAcc, element) => {
                    classAcc[element] = '';  
                    return classAcc;
                }, {});

                acc[extraClassKey]['visible'] = false;
                return acc;
            }, {});
        }
        map[key] = portalValueMap;
    });

    return map;
}

//Overwrites initial map in case of new elements. This is done using the mastermap
//Furthermore it sets the values corectly
export const updateMap = (givenMap, masterMap) => {
    const map = {};

    Object.keys(masterMap).forEach(key => {
        const portalKey = masterMap[key];

        const editableElements = portalKey.editableValues || [];
        const extraClasses = portalKey.extraClasses || {};

        const existingEditableValues = givenMap?.[key]?.editableValues || {};
        const existingExtraClasses = givenMap?.[key]?.extraClasses || {};

        const editableValues = { ...existingEditableValues };

        Object.entries(editableElements).forEach(([groupKey, value]) => {
            if (typeof value === 'object') {
                // Grouped format
                editableValues[groupKey] = editableValues[groupKey] || {};

                Object.keys(value).forEach(subKey => {
                    if (!(subKey in editableValues[groupKey])) {
                        editableValues[groupKey][subKey] = '';
                    }
                });
            } else {
                // Flat format
                if (!(groupKey in editableValues)) {
                    editableValues[groupKey] = '';
                }
            }
        });

        const extraClassesMap = {};

        Object.entries(extraClasses).forEach(([extraClassKey, editableElementsForClass]) => {
            const existingClassValues = existingExtraClasses[extraClassKey] || {};

            extraClassesMap[extraClassKey] = { ...existingClassValues };

            Object.keys(editableElementsForClass).forEach(elementKey => {
                if (!(elementKey in extraClassesMap[extraClassKey])) {
                    extraClassesMap[extraClassKey][elementKey] = '';
                }
            });

            extraClassesMap[extraClassKey]['visible'] = existingClassValues.hasOwnProperty('visible') 
                ? existingClassValues['visible'] 
                : false;
        });

        map[key] = { editableValues };
        if (Object.keys(extraClassesMap).length > 0) {
            map[key].extraClasses = extraClassesMap;
        }
    });

    return map;
};


export const transformMap = (data) => {
    if(data !== null) {
        let updatedData = {};
        for (let mapKey in masterStyleMap) {
            updatedData[mapKey] = updateMap(data[mapKey],masterStyleMap[mapKey]);
        }
    
        return updatedData;
    }
    return masterStyleMap;
  };

export const transformValue = (value) => {
    if (value && value.hex !== undefined && value.opacity !== undefined) {
        const rgb = hexToRgb(value.hex);
        if (rgb) {
            return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${value.opacity})`;
        }
    }
    if(value.length === 0) {
        return null;
    }
    return value;
};

export const transformGradient = (value) => {
    if(value.visible) {
        if(!value.gradient) {
            return "radial-gradient(circle, rgba(255,255,255,0) 0%, rgba(255,255,255,.25) 100%)";
        } else {
            const { type, angle, colorStops } = value.gradient;
            const gradientStr = colorStops
                .map((stop) => `rgba(${hexToRgb(stop.hex)},${stop.opacity}) ${stop.position * 100}%`)
                .join(", ");
            return `${type}-gradient(${type === "radial" ? "circle" : angle+"deg" ?? 0+"deg"}, ${gradientStr})`;
        }
    }
    return "radial-gradient(circle, rgba(255,255,255,0) 0%, rgba(255,255,255,0.15) 100%)";
}

export const transformEditableValues = (editableValues) => {
    const transformedValues = {};

    for (const key in editableValues) {
        const value = editableValues[key];

        if(key === editableElements.SPIRAL){
            transformedValues["background"] = transformGradient(value);
        } else {

            const transformed = transformValue(value);
            if (transformed != null) {
                transformedValues[key] = transformed;
            }
            // transformedValues[key] = transformValue(value);
        }
    }

    return transformedValues;
};

export const masterStyleMap = {
    [page_keys.EDITREGISTERRETURN]: initialCreateMap(editRegisterReturnMap),
    [page_keys.EDITORDERLINESRETURN]: initialCreateMap(editOrderLinesMap),
    [page_keys.EDITORDERLINESCONFIRM] : initialCreateMap(editOrderLinesConfirmMap),
    [page_keys.EDITRETURNSUBMITTED] : initialCreateMap(editReturnSubmittedMap),
    [page_keys.RETURNSTATUSPAGE] : initialCreateMap(editReturnStatusPageMap),
    [page_keys.DEFAULTDATA]: defaultData
};

export const masterAssistMap = {
    [page_keys.EDITREGISTERRETURN]: editRegisterReturnMap,
    [page_keys.EDITORDERLINESRETURN]: editOrderLinesMap,
    [page_keys.EDITORDERLINESCONFIRM] : editOrderLinesConfirmMap,
    [page_keys.EDITRETURNSUBMITTED] : editReturnSubmittedMap,
    [page_keys.RETURNSTATUSPAGE] : editReturnStatusPageMap,
    [page_keys.DEFAULTDATA]: defaultData
};
